import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { CircleNotch, TrendUp, TrendDown, Lightning, ArrowSquareOut, Books, Lock, Crown } from "@phosphor-icons/react";
import { tradingViewStockUrl, screenerStockUrl } from "../lib/tradingview";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : "—");

export default function SectorStocksExpander({ sectorSymbol, sectorName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sort, setSort] = useState("volume_multiple");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get(`/sectors/${encodeURIComponent(sectorSymbol)}/stocks`, { params: { sort, limit: 40 } })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sectorSymbol, sort]);

  const rows = data?.top || [];
  const isPremiumView = data?.is_premium_view !== false;     // default true for legacy
  const lockedCount = data?.locked_count || 0;
  const updatedAt = data?.updated_at
    ? new Date(data.updated_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
    : "—";

  return (
    <AnimatePresence initial={false}>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.25 }}
        className="overflow-hidden border-t border-subtle bg-app/60"
        data-testid={`sector-stocks-${sectorSymbol}`}
      >
        <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-2 border-b border-subtle">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            /// {sectorName} · {data?.count ?? "—"} stocks · {data?.cached ? "cached" : "live"} · {updatedAt}
          </div>
          <div className="flex items-center gap-1">
            {[
              { k: "volume_multiple", label: "Volume" },
              { k: "change_pct", label: "Gainers" },
              { k: "symbol", label: "A–Z" },
            ].map((t) => (
              <button
                key={t.k}
                type="button"
                onClick={() => setSort(t.k)}
                data-testid={`sort-${sectorSymbol}-${t.k}`}
                className={
                  "px-2.5 py-1 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest transition " +
                  (sort === t.k ? "bg-mod-soft border-mod-soft text-mod" : "text-secondary-fg hover:bg-surface-hover hover:text-primary-fg")
                }
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-5 flex items-center gap-2 text-sm text-secondary-fg">
            <CircleNotch size={14} className="animate-spin text-mod" weight="bold" /> Loading constituents…
          </div>
        ) : error ? (
          <div className="p-5 text-sm text-neg">{error}</div>
        ) : rows.length === 0 ? (
          <div className="p-5 text-sm text-muted-fg">No stocks available for this sector.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`sector-stocks-table-${sectorSymbol}`}>
              <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-fg border-b border-subtle">
                <tr>
                  <th className="text-left px-4 py-2">#</th>
                  <th className="text-left px-4 py-2">Symbol</th>
                  <th className="text-right px-4 py-2">Price</th>
                  <th className="text-right px-4 py-2">Δ %</th>
                  <th className="text-right px-4 py-2">Volume ×</th>
                  <th className="text-right px-4 py-2 pr-5">Open</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s, i) => {
                  const pos = (s.change_pct || 0) >= 0;
                  return (
                    <tr
                      key={s.symbol}
                      className="border-b border-subtle/60 hover:bg-surface-hover"
                      data-testid={`sector-stock-row-${s.symbol}`}
                    >
                      <td className="px-4 py-2.5 font-mono text-[10px] text-muted-fg tabular-nums">{i + 1}</td>
                      <td className="px-4 py-2.5">
                        <div className="font-mono font-semibold tracking-wide text-primary-fg">{s.symbol.replace(".NS", "")}</div>
                        <div className="text-[11px] text-muted-fg truncate max-w-[16rem]">{s.name}</div>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono tabular-nums text-primary-fg">₹{fmt(s.last)}</td>
                      <td className={"px-4 py-2.5 text-right font-mono tabular-nums " + (pos ? "text-pos" : "text-neg")}>
                        <span className="inline-flex items-center gap-1 justify-end">
                          {pos ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />}
                          {pos ? "+" : ""}{(s.change_pct || 0).toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <span
                          className={
                            "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-mono tabular-nums " +
                            ((s.volume_multiple || 0) >= 2
                              ? "bg-pos-soft border-pos-soft text-pos"
                              : "bg-surface border-subtle text-secondary-fg")
                          }
                        >
                          <Lightning size={9} weight="fill" />
                          {(s.volume_multiple || 0).toFixed(2)}×
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right pr-5">
                        <div className="inline-flex items-center gap-1.5">
                          <a
                            href={tradingViewStockUrl(s.symbol)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`tv-${s.symbol}`}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:bg-surface-hover hover:text-pos hover:border-pos/40 transition"
                            title={`Open ${s.symbol.replace(".NS", "")} chart on TradingView`}
                          >
                            <ArrowSquareOut size={11} weight="bold" />
                            TV
                          </a>
                          <a
                            href={screenerStockUrl(s.symbol)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`screener-${s.symbol}`}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:bg-surface-hover hover:text-mod hover:border-mod/40 transition"
                            title={`Open ${s.symbol.replace(".NS", "")} fundamentals on Screener.in`}
                          >
                            <Books size={11} weight="bold" />
                            SCR
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {!isPremiumView && lockedCount > 0 && (
              <div
                className="relative border-t border-pos/40 bg-gradient-to-b from-app/60 to-pos-soft/10 px-4 py-5 text-center"
                data-testid={`sector-stocks-locked-${sectorSymbol}`}
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Lock size={14} weight="fill" className="text-pos" />
                  <span className="text-[11px] font-mono uppercase tracking-widest text-pos">
                    {lockedCount} more {sectorName} stocks locked
                  </span>
                </div>
                <p className="text-[12px] text-secondary-fg mb-3 max-w-md mx-auto">
                  Free users see the top 1 stock per sector. Unlock the full
                  constituent list (price · volume × · sortable) with the ₹5 / 5-day trial.
                </p>
                <Link
                  to="/pricing"
                  data-testid={`sector-stocks-upgrade-${sectorSymbol}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm text-[11px] font-mono uppercase tracking-widest font-bold text-black bg-pos hover:opacity-90 transition shadow-[0_0_18px_rgba(40,255,180,0.4)]"
                >
                  <Crown size={11} weight="fill" />
                  Unlock for ₹5
                </Link>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
